import { Controller } from "@hotwired/stimulus";

/**
 * Phone Format Controller
 *
 * Formats phone numbers as (XXX) XXX-XXXX as the user types.
 * Also formats existing values on page load.
 *
 * Usage:
 *   <input type="text" data-controller="phone-format" data-action="input->phone-format#format">
 *
 * Or on a parent element to format multiple phone inputs:
 *   <div data-controller="phone-format">
 *     <input type="text" data-phone-format-target="input" data-action="input->phone-format#format">
 *   </div>
 */
export default class extends Controller {
  static targets = ["input"];

  connect() {
    // Format any existing values on page load
    if (this.hasInputTarget) {
      this.inputTargets.forEach((input) => this.formatInput(input));
    } else if (this.element.tagName.toUpperCase() === "INPUT") {
      this.formatInput(this.element);
    }
  }

  format(event) {
    this.formatInput(event.target);
  }

  formatInput(input) {
    const cursorPosition = input.selectionStart;
    const originalValue = input.value;

    // Count digits before cursor in original value
    const digitsBeforeCursor = originalValue.substring(0, cursorPosition).replace(/\D/g, "").length;

    // Extract only digits
    let digits = input.value.replace(/\D/g, "");

    // Remove leading 1 if present (for 11-digit numbers)
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.substring(1);
    }

    // Limit to 10 digits
    digits = digits.substring(0, 10);

    // Format the phone number
    let formatted = "";
    if (digits.length > 0) {
      formatted = "(";
      formatted += digits.substring(0, 3);

      if (digits.length >= 3) {
        formatted += ") ";
        formatted += digits.substring(3, 6);

        if (digits.length >= 6) {
          formatted += "-";
          formatted += digits.substring(6, 10);
        }
      }
    }

    // Only update if the value changed
    if (input.value !== formatted) {
      input.value = formatted;

      // Find new cursor position by counting digits in formatted value
      let newCursor = 0;
      let digitsCounted = 0;

      for (let i = 0; i < formatted.length && digitsCounted < digitsBeforeCursor; i++) {
        newCursor = i + 1;
        if (/\d/.test(formatted[i])) {
          digitsCounted++;
        }
      }

      // Set cursor position (only if input is focused)
      if (document.activeElement === input) {
        input.setSelectionRange(newCursor, newCursor);
      }
    }
  }

  /**
   * Returns the raw digits from a formatted phone number
   * Useful for form submission if you need the unformatted value
   */
  getDigits(input) {
    let digits = input.value.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.substring(1);
    }
    return digits;
  }
}
